// apps/web/src/render/RecipeRenderEngine.tsx
//
// Phase 11.2 Plan 02 (Wave 1) — the form-context-free render engine, extracted
// VERBATIM from SpreadRenderPaneContents. This is a MOVE, not a rewrite: the
// load-bearing body (widgetStates context-filter, render-time constraint
// evaluation, the RecipeOptionGroup overlay, and the option-set single-render
// skip) lifts unchanged out of SpreadRenderPane.tsx. Only the form-state
// coupling and the recipe data-fetch stay behind in the thin adapter; the
// engine takes a resolved recipe + plain-value interaction inputs as props.
//
// PROVIDER-AGNOSTIC (CONTEXT A2): the engine renders INSIDE a caller-supplied
// recipe-state provider — it reads the state context via useContext but does
// NOT wrap its own. The three callers (the spread form adapter, the Author
// preview, the two view routes) each scope their own provider so widget state
// is shared/segregated per call site, and Phase 11.3 injects the persistence
// strategy at that boundary. Making the engine self-wrap a provider is the
// documented anti-pattern (it would defeat per-caller state scoping).
//
// W2 OPTION-SET SINGLE-RENDER RULE: RecipeOptionGroup owns option-set rendering.
// The widget loop SKIPS TemplateRenderer for any widget whose templateId ===
// "option-set" — without the skip, every option-set instance renders twice
// (once via RecipeOptionGroup's internal map, once via the templateRenderers
// Map entry). RecipeRenderEngine.test.tsx Test 7 asserts cardinality === 1.
//
// REND-05: the render-time useSpreadConstraintEvaluation call feeds the
// evalResult into RecipeOptionGroup, which surfaces the constraint AST
// `explanation` (vocab-substituted) in the disabled-option tooltip. The overlay
// is preserved unchanged by the move.
//
// META-07 / C1 GENERALITY: this file contains ZERO recipe-specific identifiers.
// Every read is generic — `recipe.widgets`, `templateId`, `widgetStates`. Keep
// it that way; recipe specifics ride in the DATA (the recipe body), never here.
//
// MISSION CRITICAL: the engine does NOT fetch. It receives a resolved recipe
// prop; the per-widget configSchema.safeParse inside TemplateRenderer is the
// defense-in-depth re-validation surface (unchanged by the move).

import type { Constraint, RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import { AlertCircle, Loader2 } from "lucide-react";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RecipeOptionGroup, type RecipeShape } from "@/pages/spread/RecipeOptionGroup";
import { useSpreadConstraintEvaluation } from "@/pages/spread/useSpreadConstraintEvaluation";
import { RecipeStateContext } from "@/widgets/recipe-state-provider";
import { TemplateRenderer } from "@/widgets/template-renderer";
import { PrintModeSwitcher } from "./print-mode-switcher";
import { PrintRoot } from "./print-root";

// ---------------------------------------------------------------------------
// RecipeRenderEngine — render-time constraint evaluation + widget rendering.
// Lives INSIDE a caller-supplied recipe-state provider so useContext picks up
// the state Map. Takes ONLY the resolved recipe + plain-value interaction inputs;
// it never sees loading/error/fetch state (those belong to the adapter / the
// co-located status panels below).
// ---------------------------------------------------------------------------

export interface RecipeRenderEngineProps {
  recipe: RecipeDefinitionV1;
  batchSets: ReadonlyArray<{ recipeId?: string }>;
  activeBatchSetIndex: number;
}

export function RecipeRenderEngine({
  recipe,
  batchSets,
  activeBatchSetIndex,
}: RecipeRenderEngineProps): React.ReactElement {
  // widgetStates from the recipe-state context. Initial mount = empty
  // Map; subsequent renders pick up state from interactive widgets. The
  // provider's value type (WidgetState union) includes `undefined` for the
  // static-note / ranked-tier pure-display widgets — the evaluator's
  // EvalContext.widgetStates Map type narrows to non-undefined values, so we
  // filter out any undefined entries here.
  const stateCtx = useContext(RecipeStateContext);
  const widgetStates: ReadonlyMap<string, Readonly<Record<string, unknown>>> = (() => {
    if (stateCtx === null) return new Map();
    const out = new Map<string, Readonly<Record<string, unknown>>>();
    for (const [id, state] of stateCtx.states) {
      if (state !== undefined) {
        out.set(id, state as Readonly<Record<string, unknown>>);
      }
    }
    return out;
  })();

  // Render-time constraint evaluation — CSTR-02 closure. recipe.constraints
  // may be undefined per the recipe-v1 schema (.optional()); pass an empty
  // array in that case (evaluator returns empty EvaluationResult; all options
  // render enabled).
  const constraints: ReadonlyArray<Constraint> = recipe.constraints ?? [];
  const evalResult = useSpreadConstraintEvaluation(constraints, {
    recipe,
    batchSets,
    activeBatchSetIndex,
    widgetStates,
  });

  // Structural adapter for RecipeOptionGroup — it consumes a RecipeShape
  // (subset of RecipeDefinitionV1). Map only the fields it reads.
  const optionGroupRecipe: RecipeShape = {
    id: (recipe as RecipeDefinitionV1 & { id?: string }).id ?? "spread-recipe",
    widgets: (recipe.widgets ?? []).map((w) => ({
      instanceId: w.instanceId,
      templateId: w.templateId,
      name: w.name,
      config: w.config,
    })),
  };

  const widgets = recipe.widgets ?? [];

  // 11.8 (Strand 3) — the active print-mode id, lifted here so the print-mode-
  // switcher (a .no-print on-screen control) and the #print-root agree on the
  // active mode. Seeded from the FIRST printModes[] entry (the reference default);
  // absent printModes → undefined (the switcher trigger + print-root both hide).
  const printModes = recipe.printModes ?? [];
  const [activePrintModeId, setActivePrintModeId] = useState<string | undefined>(
    () => printModes[0]?.id,
  );

  return (
    <>
      <RecipeOptionGroup recipe={optionGroupRecipe} evalResult={evalResult} />
      {widgets.map((widget) => {
        // W2 single-render rule: RecipeOptionGroup owns option-set rendering.
        // Skip the option-set template here to prevent the v1 double-render.
        // TemplateRenderer's option-set entry remains in the templateRenderers
        // Map (other consumers, e.g. recipe-preview, still use it) — only this
        // call site bypasses it.
        if (widget.templateId === "option-set") return null;
        return <TemplateRenderer key={widget.instanceId} widget={widget} recipe={recipe} />;
      })}
      {/* 11.8 (Strand 3) — the print surface. Mounted ONCE here (inside the
          caller's provider, a top-level sibling of the live tree), gated on
          printModes presence. The mode-switcher is a .no-print on-screen control
          that drives the active mode; #print-root is display:none on screen and
          becomes the visible document under @media print (globals.css). The
          switcher selection lifts into activePrintModeId, which the print-root
          reads — they agree on the active mode. */}
      {printModes.length > 0 ? (
        <>
          <PrintModeSwitcher
            modes={printModes}
            activeModeId={activePrintModeId}
            onSelect={setActivePrintModeId}
          />
          <PrintRoot recipe={recipe} activeModeId={activePrintModeId} />
        </>
      ) : null}
    </>
  );
}
RecipeRenderEngine.displayName = "RecipeRenderEngine";

// ---------------------------------------------------------------------------
// Co-located fetch-state presentational panels (B3). These are NOT rendered by
// the engine itself — they are pure presentational components the CALLERS (the
// spread adapter, the two view pages) render around the engine so every render
// surface shows identical empty / loading / error / not-found states. Lifted
// from SpreadRenderPane.tsx's inline branches (L102-133) so the new view pages
// reuse them rather than re-implementing the chrome.
// ---------------------------------------------------------------------------

// EMPTY — no batch set has a selected recipe yet (the adapter's
// activeBatchSetIndex < 0 branch). Mirror of SpreadRenderPane.tsx L103-107.
export function RenderEmpty(): React.ReactElement {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
      Select a recipe to start rendering.
    </div>
  );
}
RenderEmpty.displayName = "RenderEmpty";

// LOADING — the recipe query is pending. Mirror of SpreadRenderPane.tsx L111-116.
export function RenderLoading(): React.ReactElement {
  return (
    <div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
      <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
      <span>Loading recipe…</span>
    </div>
  );
}
RenderLoading.displayName = "RenderLoading";

// ERROR — the recipe query failed for a non-404 reason. Mirror of
// SpreadRenderPane.tsx L121-131 (destructive Alert). The view pages map a 404 to
// RenderNotFound below and route everything else here.
export interface RenderErrorProps {
  error?: unknown;
}

export function RenderError({ error }: RenderErrorProps): React.ReactElement {
  return (
    <div className="p-4">
      <Alert variant="destructive">
        <AlertCircle size={16} />
        <AlertTitle>Couldn't load the recipe</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : "Unknown error"}
        </AlertDescription>
      </Alert>
    </div>
  );
}
RenderError.displayName = "RenderError";

// NOT-FOUND — the B3 friendly "not found / no access" panel the view pages key
// on when api() throws ApiError with status 404 (the server 404s on read-deny
// for enumeration defense, so 404 covers both genuinely-missing and
// not-authorized). Neutral-but-clear, with a return affordance — mirrors the
// RecipeReviewPage 404 Alert (its "Return to recipes" Link).
//
// LW-02 — parameterized by `kind` so the spread view route reads "Spread not
// found" / links to /spreads instead of the recipe-framed copy. Defaults to
// "recipe" so the single-recipe view route (and any existing caller) keeps its
// current copy + /recipes link unchanged.
export interface RenderNotFoundProps {
  kind?: "recipe" | "spread";
}

export function RenderNotFound({ kind = "recipe" }: RenderNotFoundProps = {}): React.ReactElement {
  const label = kind === "spread" ? "Spread" : "Recipe";
  const backTo = kind === "spread" ? "/spreads" : "/recipes";
  const backLabel = kind === "spread" ? "Return to spreads" : "Return to recipes";
  return (
    <main aria-label={`${label} not found`} className="mx-auto max-w-md p-8">
      <Alert variant="destructive">
        <AlertCircle size={16} />
        <AlertTitle>{label} not found</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>We couldn't find that, or you don't have access to it.</p>
          <Link to={backTo} className="font-medium underline underline-offset-4">
            {backLabel}
          </Link>
        </AlertDescription>
      </Alert>
    </main>
  );
}
RenderNotFound.displayName = "RenderNotFound";
